import { useCallback, useMemo } from "react";

import { isEqual, omit } from "lodash-es";
import { useLocalStorage } from "usehooks-ts";

type UsePersistedColumnOrderOptions = {
  storageKey: string;
  /** Scopes the override inside `storageKey`, e.g. workspace plus table id */
  identifier: string;
  defaultOrder: string[];
  /**
   * Merges a stored order with the current default order so the override
   * survives schema edits. Where columns a stored order has never seen belong
   * is table-specific, so every table brings its own strategy.
   */
  reconcile: (defaultOrder: string[], storedOrder: string[]) => string[];
};

type UsePersistedColumnOrderReturn = {
  orderedColumnIds: string[];
  hasCustomOrder: boolean;
  setOrder: (columnIds: string[]) => void;
  resetOrder: () => void;
};

/**
 * Personal, per-browser column order for a table. Overlays a localStorage
 * override on top of `defaultOrder`; falls back to the default order when no
 * override is stored.
 */
export const usePersistedColumnOrder = ({
  storageKey,
  identifier,
  defaultOrder,
  reconcile,
}: UsePersistedColumnOrderOptions): UsePersistedColumnOrderReturn => {
  const [storedOrders, setStoredOrders] = useLocalStorage<
    Record<string, string[]>
  >(storageKey, {});

  const storedOrder = storedOrders[identifier];

  const { orderedColumnIds, hasCustomOrder } = useMemo(() => {
    if (!storedOrder) {
      return { orderedColumnIds: defaultOrder, hasCustomOrder: false };
    }

    const reconciledOrder = reconcile(defaultOrder, storedOrder);
    return {
      orderedColumnIds: reconciledOrder,
      hasCustomOrder: !isEqual(reconciledOrder, defaultOrder),
    };
  }, [defaultOrder, storedOrder, reconcile]);

  const setOrder = useCallback(
    (columnIds: string[]) => {
      setStoredOrders((prev) => ({ ...prev, [identifier]: columnIds }));
    },
    [setStoredOrders, identifier],
  );

  const resetOrder = useCallback(() => {
    setStoredOrders((prev) => omit(prev, identifier));
  }, [setStoredOrders, identifier]);

  return { orderedColumnIds, hasCustomOrder, setOrder, resetOrder };
};
